/**
 * Elevation Golf Lab — Cart Drawer
 * AJAX cart with Section Rendering API refresh, qty steppers, and add-to-cart interception.
 */

class CartDrawer {
  constructor() {
    this.drawer   = document.querySelector('[data-cart-drawer]');
    this.backdrop = document.querySelector('[data-cart-backdrop]');
    this.loading  = document.querySelector('[data-cart-loading]');

    if (!this.drawer) return;

    this._scrollPosition = 0;

    this._bindStatic();
    this._bindDrawer();
    this.interceptAddToCart();

    // Listen for programmatic open (e.g. header cart button)
    document.addEventListener('cart:open', () => this.open());
  }

  // ── Open / Close ────────────────────────────────────────────────────

  open() {
    this._scrollPosition = window.scrollY;
    document.body.style.overflow   = 'hidden';
    document.body.style.position   = 'fixed';
    document.body.style.top        = `-${this._scrollPosition}px`;
    document.body.style.width      = '100%';

    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.backdrop.classList.add('is-open');
    this.backdrop.setAttribute('aria-hidden', 'false');

    // Re-bind dynamic elements every open (after a refresh)
    this._bindDrawer();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.backdrop.classList.remove('is-open');
    this.backdrop.setAttribute('aria-hidden', 'true');

    document.body.style.overflow   = '';
    document.body.style.position   = '';
    document.body.style.top        = '';
    document.body.style.width      = '';
    window.scrollTo(0, this._scrollPosition);
  }

  isOpen() {
    return this.drawer.classList.contains('is-open');
  }

  // ── Loading overlay ─────────────────────────────────────────────────

  _setLoading(active) {
    if (!this.loading) return;
    this.loading.classList.toggle('is-active', active);
  }

  // ── Cart API helpers ─────────────────────────────────────────────────

  async _post(url, body) {
    const res = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body:    JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Cart request failed: ${res.status}`);
    return res.json();
  }

  async addItem(variantId, qty = 1) {
    return this._post('/cart/add.js', { id: variantId, quantity: qty });
  }

  async updateLine(lineKey, quantity) {
    return this._post('/cart/change.js', { id: lineKey, quantity });
  }

  async removeItem(lineKey) {
    return this.updateLine(lineKey, 0);
  }

  async changeQty(lineKey, delta) {
    const item = this.drawer.querySelector(`[data-line-key="${lineKey}"]`);
    if (!item) return;
    const current = parseInt(item.querySelector('[data-qty-value]')?.textContent || '1', 10);
    const next    = Math.max(0, current + delta);
    await this.updateLine(lineKey, next);
    await this.refresh();
  }

  // ── Section Rendering API refresh ───────────────────────────────────

  async refresh() {
    this._setLoading(true);
    try {
      const res  = await fetch('/?sections=cart-drawer', { headers: { 'Accept': 'application/json' } });
      const data = await res.json();
      const html = data['cart-drawer'];
      if (!html) return;

      const tmp       = document.createElement('div');
      tmp.innerHTML   = html;
      const newDrawer = tmp.querySelector('[data-cart-drawer]');

      if (newDrawer) {
        // Preserve open state
        if (this.isOpen()) newDrawer.classList.add('is-open');
        this.drawer.replaceWith(newDrawer);
        this.drawer = newDrawer;
        this.loading = this.drawer.querySelector('[data-cart-loading]');
      }

      // Re-bind delegated events on the refreshed DOM
      this._bindDrawer();
      this._updateBadge();
    } catch (e) {
      console.warn('[CartDrawer] refresh failed:', e);
    } finally {
      this._setLoading(false);
    }
  }

  // ── Update header badge ──────────────────────────────────────────────

  async _updateBadge() {
    try {
      const res  = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      const cart = await res.json();
      const count = cart.item_count;

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = count > 0 ? count : '';
        el.classList.toggle('is-visible', count > 0);
      });

      // Fire a custom event other scripts can listen to
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count, cart } }));
    } catch (e) {
      console.warn('[CartDrawer] badge update failed:', e);
    }
  }

  // ── Event binding ────────────────────────────────────────────────────

  /** Bind events that don't depend on drawer content (once, at init) */
  _bindStatic() {
    // Backdrop click closes drawer
    this.backdrop?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    // Header cart trigger (data-cart-trigger dispatches cart:open)
    document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
  }

  /** Bind events on drawer content — call after every refresh */
  _bindDrawer() {
    if (!this.drawer) return;

    // Close button(s) inside drawer
    this.drawer.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Qty decrease
    this.drawer.querySelectorAll('[data-qty-decrease]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-cart-item]');
        if (item) this.changeQty(item.dataset.lineKey, -1);
      });
    });

    // Qty increase
    this.drawer.querySelectorAll('[data-qty-increase]').forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-cart-item]');
        if (item) this.changeQty(item.dataset.lineKey, +1);
      });
    });

    // Remove item
    this.drawer.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const item = btn.closest('[data-cart-item]');
        if (!item) return;
        this._setLoading(true);
        try {
          await this.removeItem(item.dataset.lineKey);
          await this.refresh();
        } finally {
          this._setLoading(false);
        }
      });
    });

    // Cross-sell add buttons
    this.drawer.querySelectorAll('[data-cs-add]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = parseInt(btn.dataset.variantId, 10);
        if (!variantId) return;
        btn.disabled = true;
        this._setLoading(true);
        try {
          await this.addItem(variantId, 1);
          await this.refresh();
        } catch (e) {
          console.warn('[CartDrawer] cross-sell add failed:', e);
          btn.disabled = false;
        } finally {
          this._setLoading(false);
        }
      });
    });
  }

  // ── Add-to-cart interception ─────────────────────────────────────────

  interceptAddToCart() {
    // Shopify's analytics counts both a /cart/add form submit and the /cart/add.js fetch below, so a
    // submit that reaches it logs every add-to-cart twice in GA4. Cancelling the submit button's click
    // (Enter in a field fires that same click) means no submit event happens at all. Stopping the
    // submit event instead raced Shopify's own listener and still double-counted on the live site.
    window.addEventListener('click', e => {
      const btn  = e.target.closest('button[type="submit"], input[type="submit"]');
      const form = btn && btn.form;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();
      this._addFromForm(form);
    }, true);

    // Fallback for submits that don't come from a button click (e.g. requestSubmit from other code)
    window.addEventListener('submit', e => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this._addFromForm(form);
    }, true);
  }

  async _addFromForm(form) {
    const data     = new FormData(form);
    const variantId = data.get('id');
    const quantity  = parseInt(data.get('quantity') || '1', 10);

    if (!variantId) return;

    // Optimistically disable submit button
    const submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    this._setLoading(true);

    try {
      await this.addItem(variantId, quantity);
      await this.refresh();
      this.open();
    } catch (err) {
      console.warn('[CartDrawer] add to cart failed:', err);
      // Fall back to native form submit on failure
      form.submit();
    } finally {
      this._setLoading(false);
      if (submitBtn) submitBtn.disabled = false;
    }
  }
}

// ── Init ─────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();
});
